import { useEffect, useRef, useState } from "react";
import { adminApi } from "@/api/admin";
import type { ReconcileChange, ReconcilePreview, ReconcileStatus } from "@/types/admin";
import { ApiError } from "@/api/client";

type Phase = "idle" | "preview" | "confirm" | "running" | "done" | "errore";

export function Reconcile() {
  const [force, setForce] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<ReconcilePreview | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<ReconcileStatus | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "ok" | "err" | "warn" } | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function showToast(msg: string, type: "ok" | "err" | "warn") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  // Polling for job status
  useEffect(() => {
    if (!jobId) return;
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(async () => {
      try {
        const s = await adminApi.reconcileStatus(jobId);
        setStatus(s);
        if (s.stato !== "in_corso") {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setPhase(s.stato === "completato" ? "done" : "errore");
        }
      } catch {
        // keep polling on transient errors
      }
    }, 1500);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [jobId]);

  async function handlePreview() {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await adminApi.reconcilePreview(force);
      setPreview(res);
      setPhase("preview");
    } catch {
      showToast("Errore nel calcolo dell'anteprima. Riprovare.", "err");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleConfirmRun() {
    setRunning(true);
    try {
      const res = await adminApi.reconcileRun(force);
      setJobId(res.job_id);
      setConfirmOpen(false);
      setPhase("running");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showToast("Un ricalcolo è già in corso.", "warn");
        setConfirmOpen(false);
      } else {
        showToast("Errore nell'avvio del ricalcolo. Riprovare.", "err");
      }
    } finally {
      setRunning(false);
    }
  }

  function handleReset() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setPhase("idle");
    setPreview(null);
    setJobId(null);
    setStatus(null);
    setConfirmOpen(false);
  }

  const report = status?.report as Record<string, unknown> | null | undefined;
  const noMatch = (report?.no_match as string[] | undefined) ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold">Ricalcolo commissioni</h2>
        <p className="text-sm text-muted-foreground">
          Ricalcola e aggiorna le quote agenzia su tutte le pratiche chiuse.
        </p>
      </div>

      {/* Controls — only show when idle or preview */}
      {(phase === "idle" || phase === "preview") && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-4 max-w-lg">
          {/* Force toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <button
              type="button"
              role="switch"
              aria-checked={force}
              onClick={() => setForce((f) => !f)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
                force ? "bg-primary" : "bg-input"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                  force ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
            <span className="text-sm font-medium text-foreground">
              Forza riscrittura quote esistenti
            </span>
          </label>
          {force && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
              Le quote già presenti verranno sovrascritte anche se non sono cambiate.
            </p>
          )}

          <button
            type="button"
            disabled={previewLoading}
            onClick={handlePreview}
            className="w-full px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-medium"
          >
            {previewLoading ? "Calcolo anteprima..." : "Calcola anteprima"}
          </button>
        </div>
      )}

      {/* Phase: preview */}
      {phase === "preview" && preview && (
        <div className="space-y-4 max-w-3xl">
          {/* Summary counts */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-card border border-border rounded-xl p-4 text-center">
              <div className="text-2xl font-bold tabular-nums text-foreground">{preview.written}</div>
              <div className="text-xs text-muted-foreground mt-1">da scrivere</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4 text-center">
              <div className="text-2xl font-bold tabular-nums text-foreground">{preview.skipped}</div>
              <div className="text-xs text-muted-foreground mt-1">invariate</div>
            </div>
            <div className={`border rounded-xl p-4 text-center ${
              preview.no_match > 0
                ? "bg-amber-50 border-amber-300"
                : "bg-card border-border"
            }`}>
              <div className={`text-2xl font-bold tabular-nums ${
                preview.no_match > 0 ? "text-amber-700" : "text-foreground"
              }`}>{preview.no_match}</div>
              <div className="text-xs text-muted-foreground mt-1">senza tariffa</div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground italic">
            Stima al momento del calcolo — il dato effettivo può variare.
          </p>

          {/* Changes table */}
          {preview.changes.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="text-left py-2.5 px-4 font-medium">Agenzia</th>
                    <th className="text-left py-2.5 px-4 font-medium">Pratica</th>
                    <th className="text-center py-2.5 px-4 font-medium">SDD</th>
                    <th className="text-right py-2.5 px-4 font-medium">Quota vecchia</th>
                    <th className="text-right py-2.5 px-4 font-medium">Quota nuova</th>
                    <th className="text-right py-2.5 px-4 font-medium">Extra quota</th>
                    <th className="text-right py-2.5 px-4 font-medium">Extra ricavo</th>
                    <th className="text-center py-2.5 px-4 font-medium">Gradino</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.changes.map((c: ReconcileChange) => (
                    <tr key={c.opp_id} className="border-t border-border">
                      <td className="py-2 px-4 text-foreground">
                        {c.agenzia_name ?? <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-2 px-4 text-muted-foreground font-mono text-xs">
                        {c.opp_id}
                      </td>
                      <td className="py-2 px-4 text-center">
                        {c.sdd ? (
                          <span className="inline-block px-1.5 py-0.5 rounded text-xs font-medium border border-blue-400 text-blue-600">
                            SDD
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2 px-4 text-right tabular-nums text-muted-foreground">
                        {c.quota_old !== null ? `€${c.quota_old}` : "—"}
                      </td>
                      <td className="py-2 px-4 text-right tabular-nums font-medium text-foreground">
                        {c.quota_new !== null ? `€${c.quota_new}` : "—"}
                      </td>
                      <td className="py-2 px-4 text-right tabular-nums text-muted-foreground">
                        {c.sdd && c.extra_quota != null ? `+€${c.extra_quota}` : "—"}
                      </td>
                      <td className="py-2 px-4 text-right tabular-nums text-muted-foreground">
                        {c.sdd && c.extra_ricavo != null ? `+€${c.extra_ricavo}` : "—"}
                      </td>
                      <td className="py-2 px-4 text-center text-muted-foreground">
                        {c.tier !== null ? `G${c.tier}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.changes.length === 0 && (
            <p className="text-sm text-muted-foreground">Nessuna modifica prevista.</p>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-medium"
            >
              Conferma ed esegui
            </button>
          </div>
        </div>
      )}

      {/* Phase: running */}
      {phase === "running" && (
        <div className="bg-card border border-border rounded-xl p-6 max-w-md flex items-center gap-4">
          <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin flex-shrink-0" />
          <p className="text-sm text-foreground">Ricalcolo in corso — attendere...</p>
        </div>
      )}

      {/* Phase: done */}
      {phase === "done" && report && (
        <div className="space-y-4 max-w-md">
          <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-3">
            <h3 className="text-base font-semibold text-green-800">
              Ricalcolo completato
            </h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-xl font-bold tabular-nums text-green-800">
                  {(report?.written as number | undefined) ?? 0}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">scritte</div>
              </div>
              <div>
                <div className="text-xl font-bold tabular-nums text-foreground">
                  {(report?.skipped as number | undefined) ?? 0}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">invariate</div>
              </div>
              <div>
                <div className={`text-xl font-bold tabular-nums ${
                  noMatch.length > 0
                    ? "text-amber-700"
                    : "text-foreground"
                }`}>
                  {noMatch.length}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">senza tariffa</div>
              </div>
            </div>
          </div>

          {/* no_match ids */}
          {noMatch.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium text-amber-700">
                Pratiche senza tariffa — sistemare il listino:
              </p>
              <ul className="space-y-0.5">
                {noMatch.map((id: string) => (
                  <li key={id} className="font-mono text-xs text-foreground">
                    {id}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            type="button"
            onClick={handleReset}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Nuovo ricalcolo
          </button>
        </div>
      )}

      {/* Phase: errore */}
      {phase === "errore" && (
        <div className="space-y-3 max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-xl p-5">
            <h3 className="text-base font-semibold text-destructive mb-1">
              Ricalcolo fallito
            </h3>
            <p className="text-sm text-muted-foreground">
              {status?.errore ?? "Errore sconosciuto."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Riprova
          </button>
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !running && setConfirmOpen(false)}
        >
          <div
            className="bg-card text-foreground rounded-xl max-w-md w-full p-6 shadow-xl border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-2">Conferma ricalcolo</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Stai per ricalcolare e aggiornare le quote commissioni.
            </p>
            <div className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm mb-1">
              <span className="font-medium">Modalità:</span>{" "}
              {force ? (
                <span className="text-amber-700 font-medium">
                  forza riscrittura quote esistenti
                </span>
              ) : (
                "aggiorna solo quote mancanti o cambiate"
              )}
            </div>
            {preview && (
              <p className="text-xs text-muted-foreground mb-4">
                Anteprima: {preview.written} da scrivere · {preview.skipped} invariate ·{" "}
                {preview.no_match} senza tariffa
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={running}
                onClick={() => setConfirmOpen(false)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="button"
                disabled={running}
                onClick={handleConfirmRun}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {running ? "Avvio..." : "Esegui ricalcolo"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg text-sm shadow-lg border ${
            toast.type === "ok"
              ? "bg-green-100 text-green-800 border-green-200"
              : toast.type === "warn"
              ? "bg-amber-100 text-amber-800 border-amber-200"
              : "bg-red-100 text-red-800 border-red-200"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
